import React, {useState, useEffect} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import {
  Drawer,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel
} from '@material-ui/core';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import { BackdropCircular } from 'components';
import { getGiftCodesApi } from 'api';
import { parseSearchParams } from 'helpers/customers';
import httpBuildQuery from 'http-build-query';
import useLocalStorageTableCells from 'hooks/useLocalStorageTableCells';
import { useStyles } from '../styles';
import GiftCodesTableToolbar from './GiftCodesTableToolbar';
import GiftCodesTableRow from './GiftCodesTableRow';
import GiftCodesTableFilterDrawer from './GiftCodesTableFilterDrawer';


const defaultTableCells = [
  {
    label: 'Id',
    sortField: 'giftcard_id',
    display: true,
  },
  {
    label: 'Code',
    display: true,
  },
  {
    label: 'Status',
    sortField: 'status',
    display: true,
  },
  {
    label: 'Balance',
    display: true,
  },
  {
    label: 'Delivery',
    sortField: 'delivery_method',
    display: true,
  },
  {
    label: 'Created Date',
    sortField: 'created_at',
    display: true,
  },
  {
    label: 'Customer',
    sortField: 'customer_name',
    display: true,
  },
  {
    label: 'Action',
    display: true,
  }
];

const GiftCodesTable = () => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const history = useHistory();
  const paramsFromUrl: IParamsFromUrl = parseSearchParams(location.search);
  const {giftCodesList, giftCodesListCount, isGiftCodesListLoading} = useSelector((state: IReduxState) => state.gift_card);
  const [sortDirection, setSortDirection] = useState(paramsFromUrl.sortDirection || 'desc');
  const [sortField, setSortField] = useState(paramsFromUrl.sortField || 'created_at');
  const [currentPage, setCurrentPage] = useState(paramsFromUrl.currentPage || 1);
  const [rowsPerPage, setRowsPerPage] = useState(paramsFromUrl.rowsPerPage || 10);
  const [openFilter, setOpenFilter] = useState(false);
  const [filterConditions, setFilterConditions] = useState<IFilterConditions>(paramsFromUrl.filterConditions || {});
  const [searchConditions, setSearchConditions] = useState(paramsFromUrl.searchConditions || {});
  const { headTableCells, setHeadTableCells } = useLocalStorageTableCells('GiftCodesTableCells', defaultTableCells);

  useEffect(() => {
    const queryParams = {
      filterConditions,
      searchConditions,
      currentPage,
      sortDirection,
      sortField,
      rowsPerPage,
    };

    const queryString = httpBuildQuery(queryParams);
    history.replace({
      pathname: location.pathname,
      search: queryString,
    });
  }, [filterConditions, searchConditions, currentPage, sortDirection, sortField, rowsPerPage]);

  useEffect(() => {
    getGiftCodesApi({
      dispatch,
      sortDirection,
      sortField,
      pageSize: rowsPerPage,
      currentPage,
      filterConditions: Object.values(filterConditions)
        .filter((condition) => condition.value !== ''),
      searchConditions
    });
  }, [
    sortField,
    sortDirection,
    rowsPerPage,
    currentPage,
    filterConditions,
    searchConditions
  ]);

  const onSearch = (searchValue, searchField) => {
    const condition = {
      field: searchField,
      value: searchValue.length ? `%${searchValue}%` : '',
      conditionType: 'like'
    };
    setSearchConditions(searchValue.length ? condition : {});
    setCurrentPage(1);
  };

  const handleRequestSort = (event, property) => {
    const isDesc = sortField === property && sortDirection === 'desc';
    setSortDirection(isDesc ? 'asc' : 'desc');
    setSortField(property);
  };

  const handleChangePage = (event, newPage) => {
    setCurrentPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(1);
  };

  return (
    <>
      <div className={`p-5 ${classes.toolbarContainer}`}>
        <GiftCodesTableToolbar
          onSearch={onSearch}
          setOpenFilter={setOpenFilter}
          filterConditions={filterConditions}
          tableCells={headTableCells}
          setHeadTableCells={setHeadTableCells}
        />
      </div>
      <div className="table-container">
        <TableContainer>
          <Table aria-label="collapsible table" className={classes.table}>
            <TableHead>
              <TableRow>
                {headTableCells.map((cell, index) => {
                  if (!cell.display) {
                    return null;
                  }

                  return (
                    <TableCell
                      key={index}
                      className="min-w-80"
                    >
                      {cell.sortField ? (
                        <TableSortLabel
                          active={sortField === cell.sortField}
                          IconComponent={sortDirection === 'asc' ? ArrowUpwardIcon : ArrowDownwardIcon}
                          onClick={(event) => handleRequestSort(event, cell.sortField)}
                        >
                          {cell.label}
                        </TableSortLabel>
                      ) : (
                        cell.label
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {!giftCodesListCount ?
                <TableRow>
                  <TableCell colSpan={Object.keys(headTableCells).length + 1} align="center">
                    No matching gift codes found.
                  </TableCell>
                </TableRow>
                :
                giftCodesList.map((el, index) => {
                  const labelId = `enhanced-table-checkbox-${index}`;

                  return (
                    <GiftCodesTableRow
                      key={el.giftcard_id}
                      giftCodeData={el}
                      headTableCells={headTableCells}
                      labelId={labelId}
                    />
                  );
                })
              }
            </TableBody>
          </Table>
        </TableContainer>
        {!!giftCodesListCount &&
          <TablePagination
            rowsPerPageOptions={[10, 20, 40, 80, 100]}
            component="div"
            className={classes.paginationContainer}
            count={giftCodesListCount}
            rowsPerPage={Number(rowsPerPage)}
            page={Number(currentPage) - 1}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        }
      </div>
      <Drawer
        anchor="right"
        open={openFilter}
        onClose={() => setOpenFilter((openFilter) => !openFilter)}
        PaperProps={{ style: { maxWidth: '400px' } }}
      >
        <GiftCodesTableFilterDrawer
          setOpenFilter={setOpenFilter}
          setFilterConditions={setFilterConditions}
          filterConditions={filterConditions}
        />
      </Drawer>
      <BackdropCircular loading={isGiftCodesListLoading} />
    </>
  );
};

export default GiftCodesTable;
